/**
 * ConfirmPH Interactive Sales Demo: v1 scenarios
 *
 * Rules:
 *  - Everything is FICTIONAL. Names, businesses, plates, phone numbers.
 *  - Every scenario is fully self-contained.  No network calls, no writes.
 *  - Only reference ConfirmPH features that actually exist in the product.
 *  - Any planned feature must be marked `{ planned: true }` on its panel.
 *
 * Shape:
 *   {
 *     id, category, title, subtitle, keywords[],
 *     channel: { kind, business, customer, vehicle }  // optional (intake demos)
 *     chat:    [{ from:'customer'|'staff'|'system', text, at }]  // ms after start
 *     salesNotes: string[]     // hidden in Manager View
 *     panels:  [
 *       { key, kind, title, hint, sales, warn?, ok?, data, action?, planned? }
 *     ]
 *   }
 */

const P = (obj) => obj;   // syntactic marker

/* -----------------------------------------------------------------
 * BOOKING INTAKE
 * ----------------------------------------------------------------- */

const messenger = {
  id: "messenger-inquiry",
  category: "intake",
  title: "Customer contacted us through Messenger",
  subtitle: "Messenger inquiry → Notepad → Draft → Booking → Confirmation",
  keywords: ["messenger", "facebook", "chat", "inquiry", "vios"],
  channel: { kind: "messenger", business: "Dumaguete Island Wheels", customer: "Maria Santos" },
  chat: [
    { from: "customer", text: "Hi po, available Toyota Vios June 12 to June 14?", at: 400 },
    { from: "customer", text: "Pickup Dumaguete Airport around 9am please.", at: 1600 },
    { from: "customer", text: "How much po? Can I reserve?", at: 3000 },
  ],
  salesNotes: [
    "Customer can keep using Messenger: nothing changes for them.",
    "Staff record the important details into ConfirmPH once.",
    "Both sides end up with a clear written confirmation.",
  ],
  panels: [
    P({ key: "chat",  kind: "chat-focus", title: "Customer sends Messenger inquiry",
        hint: "This is exactly the kind of message you already receive." }),
    P({ key: "note",  kind: "notepad", title: "Staff records the key details",
        hint: "Only a few structured fields: under 30 seconds.",
        data: {
          customer: "Maria Santos", contact: "Messenger", vehicle: "Toyota Vios",
          pickup: "Jun 12 · 09:00", pickup_location: "Dumaguete Airport",
          ret: "Jun 14 · 09:00", price: "₱1,700/day", notes: "Airport pickup requested.",
        }}),
    P({ key: "draft", kind: "draft", title: "Draft booking is created", action: "create-draft",
        hint: "The chat is no longer buried inside Messenger. Details are now structured.",
        data: { customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
                pickup: "Jun 12 · 09:00 · Dumaguete Airport",
                ret: "Jun 14 · 09:00 · Dumaguete Airport",
                price: "₱1,700/day", subtotal: "₱3,400",
                deposit: "₱1,000", balance: "₱2,400",
                source: "Messenger", status: "Draft" }}),
    P({ key: "book",  kind: "booking", title: "Booking created: pending confirmation",
        hint: "One-click promotion. The booking now has a shareable confirmation link.",
        action: "create-booking",
        data: { status: "Pending customer confirmation" }}),
    P({ key: "conf-sent", kind: "confirmation-sent", title: "Confirmation link generated",
        hint: "You can send this over Messenger, SMS, or copy it.",
        sales: "You still contact the customer the way you always do: we just add the link." }),
    P({ key: "cust",  kind: "customer-confirmation", title: "Preview: customer's confirmation page",
        hint: "This is what your customer sees.  Tap Confirm to simulate their action.",
        action: "customer-confirm" }),
    P({ key: "done",  kind: "confirmed", title: "Customer Confirmed",
        hint: "Both sides now have the same written details.",
        ok: "Confirmed" }),
  ],
};

const whatsapp = {
  id: "whatsapp-inquiry",
  category: "intake",
  title: "Customer contacted us through WhatsApp",
  subtitle: "WhatsApp inquiry → Notepad → Draft",
  keywords: ["whatsapp", "chat", "inquiry"],
  channel: { kind: "whatsapp", business: "Cebu Wheels & Wander", customer: "David Cruz" },
  chat: [
    { from: "customer", text: "Hi, is the Innova free July 5 to July 8?", at: 300 },
    { from: "customer", text: "5 pax including driver. Family trip.", at: 1500 },
    { from: "customer", text: "Pickup Mactan Airport.", at: 2700 },
  ],
  salesNotes: [
    "Same workflow no matter which app the customer uses.",
    "One ConfirmPH record per booking, even if the conversation spans channels.",
  ],
  panels: [
    P({ key: "chat",  kind: "chat-focus", title: "WhatsApp inquiry arrives", hint: "Different app, same intake flow." }),
    P({ key: "note",  kind: "notepad", title: "Staff records the details",
        data: { customer: "David Cruz", contact: "WhatsApp", vehicle: "Toyota Innova",
                pickup: "Jul 5 · 10:00", pickup_location: "Mactan Airport",
                ret: "Jul 8 · 10:00", price: "₱2,800/day", notes: "5 pax family." }}),
    P({ key: "draft", kind: "draft", title: "Draft booking created", action: "create-draft",
        hint: "Notice how the source is tracked: great for later analytics.",
        data: { customer: "David Cruz", vehicle: "Toyota Innova · LFR-4098",
                pickup: "Jul 5 · 10:00 · Mactan Airport",
                ret: "Jul 8 · 10:00 · Mactan Airport",
                price: "₱2,800/day", subtotal: "₱8,400",
                deposit: "₱2,500", balance: "₱5,900",
                source: "WhatsApp", status: "Draft" }}),
  ],
};

const sms = {
  id: "sms-inquiry",
  category: "intake",
  title: "Customer sent an SMS",
  subtitle: "SMS inquiry → Notepad → Draft",
  keywords: ["sms", "text message", "inquiry"],
  channel: { kind: "sms", business: "Bacolod Ride Hub", customer: "Lea Padilla" },
  chat: [
    { from: "customer", text: "Ma'am kailangan ko ng motor Aug 2 - Aug 4. NMax po.", at: 300 },
    { from: "customer", text: "Pickup sa Bacolod City.", at: 1600 },
  ],
  salesNotes: [
    "Even short SMS threads deserve a structured record.",
    "Fewer misheard phone numbers or dates.",
  ],
  panels: [
    P({ key: "chat",  kind: "chat-focus", title: "SMS inquiry arrives",
        hint: "Short messages still turn into complete bookings." }),
    P({ key: "note",  kind: "notepad", title: "Enter details into Notepad",
        data: { customer: "Lea Padilla", contact: "SMS", vehicle: "Yamaha NMax",
                pickup: "Aug 2 · 09:00", pickup_location: "Bacolod City",
                ret: "Aug 4 · 09:00", price: "₱650/day", notes: "SMS enquiry." }}),
    P({ key: "draft", kind: "draft", title: "Draft booking created", action: "create-draft",
        data: { customer: "Lea Padilla", vehicle: "Yamaha NMax · 155A-XY",
                pickup: "Aug 2 · 09:00 · Bacolod City",
                ret: "Aug 4 · 09:00 · Bacolod City",
                price: "₱650/day", subtotal: "₱1,300", deposit: "₱500", balance: "₱800",
                source: "SMS", status: "Draft" }}),
  ],
};

const phone = {
  id: "phone-call",
  category: "intake",
  title: "Customer called by phone",
  subtitle: "Phone call → Notepad → Draft",
  keywords: ["phone", "call", "voice"],
  channel: { kind: "phone", business: "Iloilo Drive PH", customer: "Michael Thompson" },
  chat: [
    { from: "system",   text: "Incoming call: Michael Thompson", at: 200 },
    { from: "customer", text: "Hi, I'm looking for an automatic car Aug 5 to Aug 8.", at: 900 },
    { from: "staff",    text: "Okay sir. Pickup where?", at: 2100 },
    { from: "customer", text: "Iloilo Airport if possible.", at: 3300 },
  ],
  salesNotes: [
    "Enter details while the call is still on speaker.",
    "You can't lose a detail if it lives in Notepad instead of memory.",
  ],
  panels: [
    P({ key: "chat",  kind: "chat-focus", title: "Answer the demo call",
        hint: "Even phone bookings get one written record." }),
    P({ key: "note",  kind: "notepad", title: "Enter call notes",
        data: { customer: "Michael Thompson", contact: "Phone",
                vehicle: "Honda City AT", pickup: "Aug 5 · 10:00",
                pickup_location: "Iloilo Airport",
                ret: "Aug 8 · 10:00", price: "₱2,000/day",
                notes: "Prefers automatic; airport pickup." }}),
    P({ key: "draft", kind: "draft", title: "Draft booking created", action: "create-draft",
        data: { customer: "Michael Thompson", vehicle: "Honda City AT · JAX-8842",
                pickup: "Aug 5 · 10:00 · Iloilo Airport",
                ret: "Aug 8 · 10:00 · Iloilo Airport",
                price: "₱2,000/day", subtotal: "₱6,000",
                deposit: "₱2,000", balance: "₱4,000",
                source: "Phone", status: "Draft" }}),
  ],
};

const walkin = {
  id: "walk-in",
  category: "intake",
  title: "Walk-in booking",
  subtitle: "Walk-in customer → Draft",
  keywords: ["walk-in", "counter", "in person"],
  channel: { kind: "walkin", business: "Isla Drive Car Rental", customer: "Carlo Dela Cruz" },
  chat: [
    { from: "system",   text: "Customer walks into the shop", at: 100 },
    { from: "customer", text: "Do you have a Honda Click available today until tomorrow?", at: 900 },
    { from: "staff",    text: "Yes sir, we can prepare it.  Let me record the booking.", at: 2100 },
  ],
  salesNotes: [
    "The same structured record even without a digital chat.",
    "No paper slip lost between shifts.",
  ],
  panels: [
    P({ key: "chat",  kind: "chat-focus", title: "Walk-in inquiry",
        hint: "Customer is standing at the counter: record it live." }),
    P({ key: "note",  kind: "notepad", title: "Enter the customer's details",
        data: { customer: "Carlo Dela Cruz", contact: "Walk-in",
                vehicle: "Honda Click", pickup: "Today · 14:00",
                pickup_location: "Shop",
                ret: "Tomorrow · 14:00", price: "₱450/day",
                notes: "Photo ID captured at counter." }}),
    P({ key: "draft", kind: "draft", title: "Draft booking created", action: "create-draft",
        data: { customer: "Carlo Dela Cruz", vehicle: "Honda Click · 771-BCD",
                pickup: "Today · 14:00 · Shop",
                ret: "Tomorrow · 14:00 · Shop",
                price: "₱450/day", subtotal: "₱450", deposit: "₱200", balance: "₱250",
                source: "Walk-in", status: "Draft" }}),
  ],
};

/* -----------------------------------------------------------------
 * BOOKING PROBLEMS
 * ----------------------------------------------------------------- */

const doubleBooking = {
  id: "double-booking",
  category: "problems",
  title: "Double booking on the same vehicle",
  subtitle: "Conflict warning and resolution",
  keywords: ["double", "conflict", "overlap", "clash"],
  business: "Dumaguete Island Wheels",
  salesNotes: [
    "ConfirmPH catches the overlap BEFORE the second booking is finalized.",
    "Important: ConfirmPH only sees bookings that were entered.",
  ],
  panels: [
    P({ key: "existing", kind: "booking",  title: "Existing booking on Toyota Vios",
        hint: "Maria's booking already exists in ConfirmPH.",
        data: { customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
                pickup: "Jun 12 · 09:00 · Dumaguete Airport",
                ret: "Jun 14 · 09:00 · Dumaguete Airport",
                source: "Messenger", status: "Confirmed" }}),
    P({ key: "second",   kind: "draft",     title: "Second inquiry arrives",
        hint: "John Miller wants the same vehicle on overlapping dates.",
        data: { customer: "John Miller", vehicle: "Toyota Vios · WSJ-2211",
                pickup: "Jun 13 · 09:00 · Dumaguete Airport",
                ret: "Jun 15 · 09:00 · Dumaguete Airport",
                price: "₱1,700/day", source: "Phone", status: "Draft" }}),
    P({ key: "warn",     kind: "conflict",  title: "Overlapping bookings on this vehicle",
        hint: "ConfirmPH highlights the overlap so it can't slip through.",
        warn: "Toyota Vios is already assigned to another booking during part of these dates.",
        data: { existing: "Maria Santos · Jun 12–14",
                incoming: "John Miller · Jun 13–15",
                options: ["Reassign vehicle", "Notify customer",
                           "Change dates", "Review calendar",
                           "Leave as unresolved draft"] }}),
    P({ key: "resolve",  kind: "draft",     title: "Reassign to another vehicle",
        hint: "Assign an available Corolla instead: problem solved.",
        data: { customer: "John Miller", vehicle: "Toyota Corolla · JHK-3312",
                pickup: "Jun 13 · 09:00 · Dumaguete Airport",
                ret: "Jun 15 · 09:00 · Dumaguete Airport",
                price: "₱1,800/day", source: "Phone", status: "Draft (reassigned)" }}),
  ],
};

const tightBuffer = {
  id: "tight-buffer",
  category: "problems",
  title: "Tight buffer between bookings",
  subtitle: "Cleaning / fuel / inspection risk",
  keywords: ["buffer", "tight", "back to back", "gap"],
  business: "Cebu Wheels & Wander",
  salesNotes: [
    "Even legal bookings can go wrong if there is no cleaning window.",
    "Show how the buffer warning prompts the business to add time.",
  ],
  panels: [
    P({ key: "A", kind: "booking", title: "Booking A returns at 10:00",
        data: { customer: "Anna Reyes", vehicle: "Mitsubishi Mirage · LMN-901",
                pickup: "Yesterday · 10:00", ret: "Today · 10:00",
                source: "Messenger", status: "Ongoing" }}),
    P({ key: "B", kind: "booking", title: "Booking B picks up at 11:00: same vehicle",
        data: { customer: "Ben Salazar", vehicle: "Mitsubishi Mirage · LMN-901",
                pickup: "Today · 11:00", ret: "Sat · 11:00",
                source: "Phone", status: "Pending" }}),
    P({ key: "warn", kind: "conflict", title: "Short turnaround between bookings",
        warn: "Only 1 hour between Booking A's return and Booking B's pickup on Mitsubishi Mirage · LMN-901.",
        hint: "The business can extend Booking B or arrange a swap.",
        data: { existing: "Anna Reyes · returns 10:00",
                incoming: "Ben Salazar · pickup 11:00",
                options: ["Reassign vehicle", "Move Booking B to 12:30", "Prepare rapid turnover"] }}),
  ],
};

const dateChange = {
  id: "customer-date-change",
  category: "problems",
  title: "Customer wants to change dates",
  subtitle: "Edit booking → re-check conflict → new version → fresh acceptance",
  keywords: ["change", "reschedule", "date", "extension", "messenger"],
  channel: { kind: "messenger", business: "Iloilo Drive PH", customer: "Grace Lim" },
  chat: [
    { from: "customer", text: "Hi po, can we change return from June 14 to June 16?", at: 400 },
    { from: "customer", text: "Same vehicle, same pickup place.", at: 1500 },
  ],
  salesNotes: [
    "Changes still flow through the same structured record.",
    "A material change creates a new booking version. The customer accepts that version; the earlier acceptance stays in the history and is never silently reused.",
  ],
  panels: [
    P({ key: "chat", kind: "chat-focus", title: "Customer requests a change" }),
    P({ key: "edit", kind: "booking", title: "Open the booking and edit",
        hint: "Update the return date and re-run the conflict check.",
        data: { customer: "Grace Lim", vehicle: "Toyota Wigo · TDN-2298",
                pickup: "Jun 12 · 09:00", ret: "Jun 16 · 09:00 (was Jun 14)",
                source: "Messenger", status: "Edited: needs re-confirmation" }}),
    P({ key: "resend", kind: "confirmation-sent",
        title: "Send the new version to Grace for acceptance",
        hint: "Changing the return date is material, so ConfirmPH issues a new version and asks Grace to accept it. Her earlier acceptance stays on record." }),
  ],
};

const awaitingConfirmation = {
  id: "awaiting-confirmation",
  category: "problems",
  title: "Booking awaiting customer confirmation",
  subtitle: "Pending confirmation → reminder → confirmed",
  keywords: ["pending", "unconfirmed", "waiting", "reminder"],
  business: "Bacolod Ride Hub",
  salesNotes: [
    "You don't have to remember to chase every customer.",
    "Follow-ups make sure the confirmation actually lands.",
  ],
  panels: [
    P({ key: "book", kind: "booking", title: "Booking pending customer confirmation",
        hint: "The link was sent 6 hours ago but Ruth hasn't confirmed yet.",
        data: { customer: "Ruth Cabales", vehicle: "Honda BR-V · KCM-6641",
                pickup: "Sat · 09:00", ret: "Sun · 18:00",
                source: "Messenger", status: "Pending customer confirmation" }}),
    P({ key: "remind", kind: "follow-up", title: "Auto-follow-up reminder",
        hint: "The system nudges the staff to check-in with the customer.",
        data: { task: "Follow up Ruth on pending confirmation",
                due: "Today · 17:00", channel: "Messenger" }}),
    P({ key: "confirmed", kind: "confirmed", title: "Ruth confirms: Customer Confirmed",
        ok: "Confirmed", action: "customer-confirm" }),
  ],
};

const forgottenBooking = {
  id: "forgotten-messenger-booking",
  category: "problems",
  title: "The Messenger booking that was never entered",
  subtitle: "Why entering matters: ConfirmPH can only see what's in ConfirmPH",
  keywords: ["forgotten", "missed", "not entered", "invisible"],
  channel: { kind: "messenger", business: "Dumaguete Island Wheels", customer: "Ella Torres" },
  chat: [
    { from: "customer", text: "Sir, reserved Vios na po ako with Kuya Jay last week.", at: 300 },
    { from: "customer", text: "Sabi niya confirmed. Pickup today 10am.", at: 1500 },
    { from: "system",   text: "There is no booking for Ella Torres in ConfirmPH.", at: 2600 },
  ],
  salesNotes: [
    "This is why every Messenger booking should be entered into ConfirmPH.",
    "Not a limitation of the product: a limitation of physics.",
    "Use this scenario to justify moving intake into Notepad.",
  ],
  panels: [
    P({ key: "chat", kind: "chat-focus", title: "Customer arrives: no record exists" }),
    P({ key: "gap",  kind: "warning", title: "ConfirmPH can't see chats that were never entered",
        warn: "No matching booking found for Ella Torres.",
        hint: "This is why Notepad exists: capture the booking as soon as it's agreed." }),
    P({ key: "fix",  kind: "notepad", title: "Recover the booking into Notepad now",
        hint: "Better late than lost: record it before the shift changes again.",
        data: { customer: "Ella Torres", contact: "Messenger",
                vehicle: "Toyota Vios", pickup: "Today · 10:00",
                notes: "Booked verbally last week; recovering into system." }}),
  ],
};

/* -----------------------------------------------------------------
 * VEHICLE PROBLEMS
 * ----------------------------------------------------------------- */

const breakdown = {
  id: "vehicle-breakdown",
  category: "vehicle",
  title: "Vehicle breakdown affects an upcoming booking",
  subtitle: "Accident report → affected booking → reconfirmation → vehicle recovery",
  keywords: ["breakdown", "vehicle", "incident", "unavailable"],
  business: "Traveler Car Rental",
  salesNotes: [
    "This is the workflow that stops a small problem from becoming a customer problem.",
    "Notice how the affected booking is surfaced automatically.",
  ],
  panels: [
    P({ key: "veh", kind: "vehicle", title: "Toyota Fortuner: Available",
        data: { plate: "PYR-5502", model: "Toyota Fortuner",
                status: "Available",
                upcoming: "Anna Reyes · Tomorrow 09:00" }}),
    P({ key: "mark", kind: "vehicle", title: "Mark vehicle as Maintenance",
        hint: "Staff reports a breakdown after this afternoon's return.",
        action: "mark-maintenance",
        data: { plate: "PYR-5502", model: "Toyota Fortuner",
                status: "Maintenance: problem reported",
                upcoming: "Anna Reyes · Tomorrow 09:00" }}),
    P({ key: "warn", kind: "warning", title: "Booking affected by vehicle issue",
        warn: "Anna Reyes' booking tomorrow at 09:00 depends on this vehicle.",
        hint: "One tap opens the affected booking with all context.",
        data: { affected: [{ customer: "Anna Reyes", when: "Tomorrow · 09:00" }],
                options: ["Reassign vehicle", "Notify customer",
                           "Offer alternate dates", "Refund deposit"] }}),
    P({ key: "swap", kind: "booking", title: "Assign replacement: Toyota Montero",
        hint: "Update the booking and notify Anna in one flow.",
        data: { customer: "Anna Reyes", vehicle: "Toyota Montero · KDQ-7710",
                pickup: "Tomorrow · 09:00", ret: "Sat · 18:00",
                source: "Messenger", status: "Vehicle changed" }}),
  ],
};

const maintenance = {
  id: "vehicle-maintenance",
  category: "vehicle",
  title: "Scheduled maintenance blocks bookings",
  subtitle: "Vehicle status → conflict avoidance",
  keywords: ["maintenance", "service", "scheduled"],
  business: "Cebu Wheels & Wander",
  salesNotes: [
    "Scheduled maintenance is planned; ConfirmPH treats it like any other overlap.",
    "Staff won't accidentally accept a booking during service.",
  ],
  panels: [
    P({ key: "veh", kind: "vehicle", title: "Nissan Almera: Service window Jul 20 to Jul 22",
        data: { plate: "AAB-2214", model: "Nissan Almera",
                status: "Maintenance planned Jul 20 – Jul 22" }}),
    P({ key: "try", kind: "draft", title: "Attempted new booking during service",
        data: { customer: "Karen Ong", vehicle: "Nissan Almera · AAB-2214",
                pickup: "Jul 21 · 09:00", ret: "Jul 22 · 18:00",
                source: "Phone", status: "Draft" }}),
    P({ key: "warn", kind: "conflict", title: "Booked vehicle unavailable",
        warn: "Nissan Almera is scheduled for maintenance Jul 20 – Jul 22.",
        hint: "Staff can offer an alternative vehicle instead.",
        data: { existing: "Maintenance · Jul 20 – Jul 22",
                incoming: "Karen Ong · Jul 21 – 22",
                options: ["Reassign vehicle", "Move dates", "Discard draft"] }}),
  ],
};

const lateReturn = {
  id: "late-return",
  category: "vehicle",
  title: "Vehicle returned late: next booking at risk",
  subtitle: "Late-return warning + follow-up",
  keywords: ["late", "return", "delay", "overdue"],
  business: "Iloilo Drive PH",
  salesNotes: [
    "Late returns are the single biggest source of stress in rental businesses.",
    "Show the manager the moment their staff will get the alert.",
  ],
  panels: [
    P({ key: "cur",  kind: "booking", title: "Booking due back at 08:00: now 10:30",
        data: { customer: "Rafael Uy", vehicle: "Suzuki Ertiga · MOP-9911",
                pickup: "Yesterday · 09:00", ret: "Today · 08:00 (overdue)",
                status: "Ongoing · overdue" }}),
    P({ key: "warn", kind: "warning", title: "Vehicle return overdue",
        warn: "Rafael Uy hasn't returned Suzuki Ertiga. Next booking begins at 12:00.",
        hint: "One tap opens the affected next booking.",
        data: { affected: [{ customer: "Zara Hernandez", when: "Today · 12:00" }],
                options: ["Notify customer (Rafael)", "Reassign vehicle for Zara's booking",
                           "Notify Zara of possible delay", "Call Rafael"] }}),
    P({ key: "fix",  kind: "follow-up", title: "Delegate the follow-up",
        hint: "Create a task with a deadline before the next pickup.",
        data: { task: "Confirm return ETA with Rafael and update Zara",
                due: "Today · 11:15", channel: "Phone → Messenger" }}),
  ],
};

const replacement = {
  id: "replacement-vehicle",
  category: "vehicle",
  title: "Prepare a replacement vehicle",
  subtitle: "Assign another vehicle & keep the customer",
  keywords: ["replacement", "swap", "substitute", "another vehicle"],
  business: "Bacolod Ride Hub",
  salesNotes: [
    "Preserving the customer is worth more than the original vehicle preference.",
    "Note how the swap is one action, not five.",
    "A different vehicle is usually a material change: the customer is asked to accept the new version, and the replacement has to pass its own legal, safety and driver checks before it can be released.",
  ],
  panels: [
    P({ key: "aff", kind: "warning", title: "Booked vehicle unavailable",
        warn: "Toyota Rush · TRX-8871 is under repair.",
        data: { affected: [{ customer: "Kim Bautista", when: "Sat · 10:00" }] }}),
    P({ key: "pick", kind: "vehicle", title: "Available replacements",
        hint: "Pick the closest match.",
        data: { plate: "MEC-4491", model: "Toyota Avanza",
                status: "Available Sat 08:00 onwards" }}),
    P({ key: "swap", kind: "booking", title: "Confirm the swap",
        hint: "Booking updated · new version sent to Kim to accept · replacement re-checked for release readiness.",
        data: { customer: "Kim Bautista", vehicle: "Toyota Avanza · MEC-4491",
                pickup: "Sat · 10:00", ret: "Sun · 18:00",
                source: "Messenger", status: "Vehicle changed: awaiting acceptance" }}),
  ],
};

const damageIncident = {
  id: "damage-incident",
  category: "vehicle",
  title: "Damage discovered on return: affects future bookings",
  subtitle: "Log incident → link affected bookings",
  keywords: ["damage", "incident", "dent", "scratch", "accident"],
  business: "Dumaguete Island Wheels",
  salesNotes: [
    "The photo evidence and incident log stay tied to the booking it came from.",
    "Future bookings on that vehicle are surfaced automatically.",
    "Recording damage never decides fault or an amount, and it never moves money on its own.",
  ],
  panels: [
    P({ key: "veh",   kind: "vehicle", title: "Ford EcoSport returned with a dent",
        data: { plate: "ZZM-1102", model: "Ford EcoSport",
                status: "In inspection" }}),
    P({ key: "log",   kind: "incident", title: "Log the incident",
        hint: "Photos + notes stay attached to this booking and vehicle.",
        data: { title: "Rear bumper dent · Return inspection",
                by: "Kuya Rey (staff)", severity: "Minor",
                actions: ["Photo evidence", "Compare with handover record", "Estimate",
                           "Ask the customer to respond", "Repair booking"] }}),
    P({ key: "warn",  kind: "warning", title: "Booking affected by vehicle issue",
        warn: "1 booking may be affected while repairs are scheduled.",
        data: { affected: [{ customer: "Sam Guevara", when: "Fri · 10:00" }],
                options: ["Reassign vehicle", "Notify customer", "Delay booking"] }}),
  ],
};

/* -----------------------------------------------------------------
 * STAFF & OPERATIONS
 * ----------------------------------------------------------------- */

const followUp = {
  id: "follow-up-reminder",
  category: "staff",
  title: "Follow-up reminder before pickup",
  subtitle: "Pending confirmation → automatic reminder → done",
  keywords: ["reminder", "follow up", "pickup"],
  business: "Cebu Wheels & Wander",
  salesNotes: [
    "The reminder finds the staff, not the other way around.",
    "Great answer to \"my staff will forget to use it.\"",
  ],
  panels: [
    P({ key: "task", kind: "follow-up", title: "Follow-up task created automatically",
        hint: "Anyone with the right permission can see and close it.",
        data: { task: "Confirm pickup logistics with David Cruz",
                due: "Today · 16:00", channel: "WhatsApp" }}),
    P({ key: "done", kind: "confirmed", title: "Task marked done · booking still Pending",
        ok: "Follow-up completed",
        hint: "Notice how the activity is logged: visible on the booking history." }),
  ],
};

const multiStaff = {
  id: "multi-staff-activity",
  category: "staff",
  title: "Multiple staff on the same booking",
  subtitle: "Activity history keeps everyone aligned",
  keywords: ["staff", "activity", "history", "audit", "multiple"],
  business: "Manila Motor Rental",
  salesNotes: [
    "Owners often ask \"who did what?\": this answers that in one screen.",
    "No accidental double-messaging the customer.",
  ],
  panels: [
    P({ key: "book", kind: "booking", title: "Booking · Toyota Hiace",
        data: { customer: "Jonas Mendoza", vehicle: "Toyota Hiace · TQP-3311",
                pickup: "Sat · 06:00", ret: "Sun · 22:00",
                source: "Messenger", status: "Confirmed" }}),
    P({ key: "hist", kind: "activity", title: "Activity history",
        hint: "Every change traceable to the staff account that made it.",
        data: { events: [
          { at: "Fri · 09:12", by: "Ana",  what: "Draft created from Messenger inquiry" },
          { at: "Fri · 09:19", by: "Ana",  what: "Security deposit ₱2,000 recorded" },
          { at: "Fri · 10:04", by: "Rey",  what: "Confirmation link sent" },
          { at: "Fri · 13:47", by: "Rey",  what: "Customer confirmed" },
          { at: "Sat · 05:30", by: "Kim",  what: "Vehicle inspection completed" },
        ]}}),
  ],
};

const upcomingPickup = {
  id: "upcoming-pickup",
  category: "staff",
  title: "Upcoming pickup: prep the vehicle",
  subtitle: "Pickup reminder → inspection checklist",
  keywords: ["upcoming", "pickup", "prep", "checklist"],
  business: "Iloilo Drive PH",
  salesNotes: [
    "Prep tasks tie back to the booking so nothing gets missed.",
  ],
  panels: [
    P({ key: "book", kind: "booking", title: "Booking · Tomorrow 09:00",
        data: { customer: "Faye Delos Santos", vehicle: "Toyota Wigo · TDN-2298",
                pickup: "Tomorrow · 09:00", ret: "Wed · 18:00",
                source: "WhatsApp", status: "Confirmed" }}),
    P({ key: "prep", kind: "follow-up", title: "Prep task created",
        data: { task: "Clean & fuel Wigo · TDN-2298 for Faye's pickup",
                due: "Tomorrow · 07:30", channel: "Shop floor" }}),
  ],
};

// Pickup handover: the moment between "booking confirmed" and "vehicle out
// the door" that the catalog had no scenario for (upcoming-pickup only
// covered the prep-task reminder, not the actual handover check). Threads
// the same booking used in messenger-inquiry so the curated Business
// homepage journey reads as one continuous story.
const pickupHandover = {
  id: "pickup-handover",
  category: "staff",
  title: "Release readiness & handover",
  subtitle: "Readiness checks, then odometer, fuel, condition and keys before the customer drives off",
  keywords: ["pickup", "handover", "odometer", "fuel", "condition", "keys", "ongoing"],
  business: "Dumaguete Island Wheels",
  salesNotes: [
    "This is the record staff and the customer both point to if a condition dispute comes up later.",
    "Completing the handover form is not the whole gate: the accepted agreement, any payment or security-deposit condition, business and vehicle legal readiness, any safety hold, and driver eligibility are each checked, and each shows its own reason if it blocks release.",
    "Booking status flips to Ongoing automatically once the vehicle is released and handover is recorded.",
  ],
  panels: [
    P({ key: "reminder", kind: "follow-up", title: "Booking in 1 hour",
        actionLabel: "Open Maria's booking",
        hint: "A second, more urgent reminder than the prep task: staff know it's time to actually bring the vehicle round.",
        data: { task: "Deliver Toyota Vios · WSJ-2211 to the pickup point for Maria Santos",
                due: "Aug 18 · 08:00 (1 hour before pickup)", channel: "Staff app" }}),
    P({ key: "book", kind: "booking", title: "Booking · Aug 18, 09:00",
        actionLabel: "Start pickup handover",
        data: { customer: "Maria Santos", vehicle: "Toyota Vios · WSJ-2211",
                pickup: "Aug 18 · 09:00", ret: "Aug 20 · 09:00",
                source: "Messenger", status: "Confirmed" }}),
    P({ key: "handover", kind: "handover", title: "Pickup Handover", type: "pickup",
        actionLabel: "Complete pickup handover",
        data: { odometer_km: 58120, fuel_level: 100,
                existing_damage_reviewed: true,
                keys_items: [
                  { item: "Primary key", released: true },
                  { item: "Spare key", released: true },
                  { item: "Registration / OR-CR", released: true },
                ],
                note: "Customer typed their name to acknowledge the vehicle's condition before driving off." }}),
    P({ key: "ongoing", kind: "confirmed", title: "Vehicle marked Ongoing",
        actionLabel: "View return workflow",
        ok: "Booking status: Ongoing",
        hint: "Staff and the customer both see the same status the moment handover is recorded." }),
  ],
};

// Return handover + the vehicle becoming available again: closes the
// lifecycle loop the catalog was missing (no scenario previously covered
// return/inspection, Completed status, or the vehicle resetting to
// Available for its next booking).
const returnAndReady = {
  id: "return-and-ready",
  category: "staff",
  title: "Return, inspection & clearing the vehicle",
  subtitle: "Return → inspection → any safety, damage or maintenance hold → available only when cleared",
  keywords: ["return", "inspection", "completed", "available", "next booking", "ready"],
  business: "Dumaguete Island Wheels",
  salesNotes: [
    "The same handover check used at pickup is used again at return: one consistent record, not two.",
    "Returned does not mean available. The vehicle goes back into the fleet only after inspection clears it, and the rental itself is closed only after any charge, claim or deposit disposition is settled.",
  ],
  panels: [
    P({ key: "handover", kind: "handover", title: "Return Inspection", type: "return",
        actionLabel: "Complete return inspection",
        data: { odometer_km: 58410, fuel_level: 100, cleaning_condition: "Clean",
                checklist: [
                  { label: "Exterior body", rating: "minor_issue", note: "Small scuff, passenger door: photographed" },
                  { label: "Tyres", rating: "ok" },
                  { label: "Interior cabin", rating: "ok" },
                  { label: "Documents / OR-CR", rating: "ok" },
                ],
                note: "Logged against the booking. Nothing is charged and no security deposit is applied until the scuff is assessed and any charge is established." }}),
    P({ key: "completed", kind: "confirmed", title: "Returned: awaiting inspection outcome",
        actionLabel: "Open vehicle record",
        ok: "Booking status: Returned, settlement pending",
        hint: "Return recorded and condition noted. The scuff is assessed next; the rental closes once anything owed is settled or written off." }),
    P({ key: "ready", kind: "vehicle", title: "Toyota Vios · WSJ-2211",
        actionLabel: "Continue to payment record",
        data: { plate: "WSJ-2211", model: "Toyota Vios",
                status: "Available: inspection cleared, no hold",
                upcoming: "Ready for the next booking" } }),
  ],
};

// Closes the loop back to the very start of the journey: the vehicle is
// available again, and right on cue a new inquiry notification comes in.
// One panel on purpose: this is a notification, not a new scenario to work
// through.
const newInquiryAlert = {
  id: "new-inquiry-alert",
  category: "staff",
  title: "New inquiry notification",
  subtitle: "The vehicle is ready again, and the next inquiry is already coming in",
  keywords: ["notification", "new inquiry", "alert", "next booking"],
  business: "Dumaguete Island Wheels",
  salesNotes: [
    "This is the same follow-up/notification surface used throughout the app: nothing new to learn.",
  ],
  panels: [
    P({ key: "alert", kind: "follow-up", title: "New inquiry received",
        actionLabel: "Open new inquiry",
        hint: "The cycle starts again: the same intake flow from the very first step handles it.",
        data: { task: "New Messenger inquiry: Toyota Vios availability, Aug 25–27",
                due: "Just now", channel: "Messenger" }}),
  ],
};

/* -----------------------------------------------------------------
 * FINANCIAL
 * ----------------------------------------------------------------- */

const depositBalance = {
  id: "deposit-balance",
  category: "financial",
  title: "Rental payment, advance, security deposit and balance",
  subtitle: "Record what was agreed, what was paid toward the rental, what is held, and what's still owed",
  keywords: ["deposit", "security deposit", "advance", "downpayment", "balance", "payment", "refund", "money", "records"],
  business: "Bacolod Ride Hub",
  salesNotes: [
    "This is a record, not a payment processor: be clear with the manager. ConfirmPH never receives or holds the money.",
    "Keep the three apart: rental payments, the advance applied to the price, and the refundable security deposit. With Finance & Books on, each becomes a balanced accounting entry.",
    "Clarify the current live implementation before promising more.",
  ],
  panels: [
    P({ key: "book", kind: "booking", title: "Booking · Toyota Vios",
        data: { customer: "Nina Alvarez", vehicle: "Toyota Vios · WSJ-2211",
                pickup: "Fri · 10:00", ret: "Sun · 18:00",
                source: "Messenger", status: "Confirmed",
                subtotal: "₱6,000", advance: "₱1,000 (toward the rental price)",
                deposit: "₱2,000 refundable security deposit", balance: "₱5,000" }}),
    P({ key: "pay",  kind: "follow-up", title: "Record remaining balance paid",
        hint: "Update the booking record: no receipt handling here.",
        data: { task: "Mark ₱5,000 rental balance received from Nina at pickup (the ₱2,000 security deposit stays held, and is refunded or applied only to an established charge)",
                due: "Fri · 10:00", channel: "Cash / GCash" }}),
    P({ key: "done", kind: "confirmed", title: "Booking fully settled in records",
        ok: "Paid: recorded",
        hint: "The activity log now shows both amounts and who recorded them." }),
  ],
};

/* -----------------------------------------------------------------
 * TRUST / PUBLIC PROFILE
 * ----------------------------------------------------------------- */

const verifiedReview = {
  id: "verified-review",
  category: "trust",
  title: "Booking-linked review",
  subtitle: "Only customers with a real ConfirmPH booking can leave a review",
  keywords: ["review", "verified", "booking-linked", "trust", "profile", "discover"],
  business: "Dumaguete Island Wheels",
  salesNotes: [
    "Booking-linked doesn't mean guaranteed: be careful with the wording.",
    "Reviews come from customers who actually booked through ConfirmPH.",
  ],
  panels: [
    P({ key: "book",   kind: "booking", title: "Recent booking · completed",
        data: { customer: "Sam Guevara", vehicle: "Toyota Vios · WSJ-2211",
                pickup: "Last week", ret: "This week", status: "Completed" }}),
    P({ key: "invite", kind: "confirmation-sent", title: "Send review invite",
        hint: "Same channel as the confirmation: one tap." }),
    P({ key: "rev",    kind: "activity", title: "Review appears on public profile",
        hint: "Trust signal, not a guarantee.",
        data: { events: [
          { at: "This week", by: "Sam Guevara",
            what: "★★★★★ · \"Booking flow was smooth. Vehicle was clean.\"" },
        ]}}),
  ],
};

/* -----------------------------------------------------------------
 * EXPORT
 * ----------------------------------------------------------------- */

export const CATEGORIES = [
  { key: "intake",     label: "Booking Intake",
    subtitle: "Messenger, WhatsApp, phone, SMS, walk-in: into structured records" },
  { key: "problems",   label: "Booking Problems",
    subtitle: "Double bookings, tight buffers, changes and forgotten chats" },
  { key: "vehicle",    label: "Vehicle Problems",
    subtitle: "Breakdowns, maintenance, late returns, replacements" },
  { key: "staff",      label: "Staff & Follow-Up",
    subtitle: "Reminders, activity history, upcoming pickups" },
  { key: "financial",  label: "Financial Records",
    subtitle: "Rental payments, advances, security deposits, refunds and balances" },
  { key: "trust",      label: "Visibility & Trust",
    subtitle: "Public profile and verified-booking reviews" },
  { key: "presence",   label: "Online Presence",
    subtitle: "Business Page and full ConfirmPH Website that customers see" },
  { key: "operations", label: "Owner & Operations",
    subtitle: "Employees, clock-in/out, fleet, checklists, warnings" },
];

// Sales-oriented index cards for the two new public-surface demos.  The
// interactive workflow itself lives in workflow/scripts.js: these entries
// only exist so the demo index page can list them.
const businessPageIndex = {
  id: "business-page",
  category: "presence",
  title: "Your public Business Page",
  subtitle: "The free public rental profile every ConfirmPH business gets",
  keywords: ["business page", "public profile", "free tier", "share link"],
  channel: { kind: "public", business: "Isla Drive Car Rental" },
  chat: [],
  salesNotes: [
    "Same page every ConfirmPH business gets on day one: no plan upgrade required.",
    "Bookings submitted here land inside your Booking Intake automatically. No shared inbox to sort.",
    "Send this link to Facebook, Instagram bio, and Google Business: one link that works everywhere.",
  ],
  panels: [],
};

const websiteIndex = {
  id: "public-website",
  category: "presence",
  title: "Your full ConfirmPH Website",
  subtitle: "The multi-page rental website your business gets on ConfirmPH",
  keywords: ["website", "mini site", "public website", "public presence", "landing page"],
  channel: { kind: "public", business: "Isla Drive Car Rental" },
  chat: [],
  salesNotes: [
    "This isn't a mockup: it's a real feature, live for enterprise ConfirmPH businesses.",
    "Home, Fleet, Booking, About, Policies, FAQ, Reviews, Contact: all editable from your dashboard.",
    "No web developer, no separate hosting, no CMS fees. It grows with your fleet automatically.",
  ],
  panels: [],
};

// ------------------------ Owner & Operations index cards -------------------
const employeesManagementIdx = {
  id: "employees-management",
  category: "operations",
  title: "Add & manage employees",
  subtitle: "Owner-only Employees page: invite staff, set permissions, view performance",
  keywords: ["employees", "staff", "invite", "permissions", "roles"],
  business: "Cebu Wheels & Wander",
  salesNotes: [
    "Only the owner sees this page. Employees only see what you tick on.",
    "Every action is attributable back to the exact employee that did it.",
  ],
  panels: [],
};
const staffAvailabilityIdx = {
  id: "staff-availability",
  category: "operations",
  title: "Clock in / out & traceable actions",
  subtitle: "Who's on the clock: routing rules and audit trail",
  keywords: ["clock in", "clock out", "attendance", "availability", "audit", "trace"],
  business: "Iloilo Drive PH",
  salesNotes: [
    "Inquiries and bookings route to a clocked-in staff with the right permission.",
    "Attendance history + per-booking activity log = full traceability.",
  ],
  panels: [],
};
const vehiclesManagementIdx = {
  id: "vehicles-management",
  category: "operations",
  title: "Add / edit / retire vehicles",
  subtitle: "Vehicles page: the tabbed Add Vehicle form and fleet capacity",
  keywords: ["vehicles", "fleet", "add", "edit", "retire"],
  business: "Bacolod Ride Hub",
  salesNotes: [
    "Add Vehicle is a tabbed form: Basic Info, Technical, Pricing, Public, Rules, Internal, Visibility.",
    "Fleet capacity is measured in points per vehicle category. Bigger fleets need a bigger plan.",
  ],
  panels: [],
};
const dailyChecklistsIdx = {
  id: "daily-checklists",
  category: "operations",
  title: "Daily checklists & inspections",
  subtitle: "Opening, closing, vehicle readiness, cleaning, handover: timestamped",
  keywords: ["checklist", "opening", "closing", "handover", "inspection", "daily"],
  business: "Cebu Wheels & Wander",
  salesNotes: [
    "Default templates come pre-seeded. Owners can edit items per business.",
    "Missed opening/closing days surface a red banner the next morning.",
  ],
  panels: [],
};
const warningsComplianceIdx = {
  id: "warnings-compliance",
  category: "operations",
  title: "Warning Center: operational risks",
  subtitle: "Late returns, unpaid security deposits, missing inspections, buffer clashes: one screen",
  keywords: ["warnings", "compliance", "risk", "overdue", "deposit", "inspection", "close"],
  business: "Manila Motor Rental",
  salesNotes: [
    "This is the owner's cockpit for anything that's currently going wrong.",
    "Every warning shows what happened, why it matters, and one clear recommended action, not just a red label.",
    "Closing a warning always asks why: Resolved, Not an issue, Accepted risk, Duplicate, or Other, so there's a real record of who closed it and when.",
    "Nothing is ever sent to a customer automatically. Notify customer only sends when staff click Send.",
  ],
  panels: [
    P({ key: "veh", kind: "vehicle", title: "Nissan Urvan NV350: Maintenance",
        data: { plate: "NUV-3350", model: "Nissan Urvan NV350",
                status: "Maintenance: reported after this morning's return",
                upcoming: "Diego Ramos · Today · 15:00" }}),
    P({ key: "warn", kind: "warning", title: "Booked vehicle unavailable",
        warn: "Nissan Urvan NV350 is currently in maintenance but is assigned to a booking today at 15:00.",
        hint: "Title, why it matters, recommended action, and the affected booking: all in one card.",
        data: { affected: [{ customer: "Diego Ramos", when: "Today · 15:00" }],
                options: ["Reassign vehicle", "Notify customer",
                           "Mark reviewed", "Add note", "Close"] }}),
    P({ key: "closed", kind: "confirmed", title: "Warning closed",
        ok: "Closed · Resolved: reassigned to Toyota HiAce · Ana Cruz, Aug 11 · 14:42" }),
  ],
};

const directSelfDrive = {
  id: "direct-self-drive-booking",
  category: "intake",
  title: "Customer books directly in ConfirmPH",
  subtitle: "Discover page → vehicle → dates → self-drive eligibility → terms → confirmation",
  keywords: ["direct booking", "self drive", "licence", "customer"],
  business: "Isla Drive Car Rental",
  salesNotes: [
    "The customer chooses self-drive and only sees renter-driver requirements that apply to that mode.",
    "Licence details and private evidence are linked to the authorized driver, not copied into a public record.",
    "A booking agreement version is accepted before the customer confirmation is created.",
  ],
  panels: [
    P({ key: "discover", kind: "booking", title: "Customer chooses a vehicle and dates", data: { status: "Availability checked", source: "ConfirmPH public business page", mode: "Self-drive" } }),
    P({ key: "driver", kind: "booking", title: "Self-drive driver details", data: { primary_driver: "Required when business policy asks for it", documents: "Driver's licence and government ID where required", evidence: "Private, uploaded evidence still needs business review" } }),
    P({ key: "terms", kind: "customer-confirmation", title: "Customer reviews the current booking agreement" }),
    P({ key: "done", kind: "confirmed", title: "Booking request recorded", ok: "Customer confirmation is linked to the accepted agreement version" }),
  ],
};

const directWithDriver = {
  id: "direct-company-driver-booking",
  category: "intake",
  title: "Customer books with a company driver",
  subtitle: "Hire a Driver → passenger details → driver-service quote → dispatch readiness",
  keywords: ["hire a driver", "company driver", "dispatch", "passenger"],
  business: "Cebu Wheels & Wander",
  salesNotes: [
    "With-driver bookings do not ask the passenger to submit a renter driver's licence.",
    "The business assigns a qualified and available company driver through the existing Task 5 dispatch flow.",
    "Driver service pricing remains separate from the vehicle rental price.",
  ],
  panels: [
    P({ key: "mode", kind: "booking", title: "Customer selects Hire a Driver", data: { service: "Business-supplied driver", renter_licence: "Not requested", passenger_count: "Captured" } }),
    P({ key: "booking", kind: "booking", title: "Business receives the booking", data: { assignment: "Pending qualified driver assignment", dispatch: "Readiness check required" } }),
    P({ key: "ready", kind: "confirmed", title: "Driver dispatch ready", ok: "Driver qualification, availability and handover are ready for staff review" }),
  ],
};

const additionalDriver = {
  id: "additional-authorized-driver",
  category: "intake",
  title: "Add an authorized self-drive driver",
  subtitle: "Primary driver → invitation → second driver's evidence → eligibility",
  keywords: ["additional driver", "authorized driver", "invitation", "evidence"],
  business: "Dumaguete Island Wheels",
  salesNotes: [
    "Each authorized driver is a separate booking-linked record.",
    "Additional driver evidence is private and never makes the driver globally visible to another business.",
  ],
  panels: [
    P({ key: "primary", kind: "booking", title: "Primary driver is recorded", data: { status: "Authorized", booking: "One rental record" } }),
    P({ key: "invite", kind: "confirmation-sent", title: "Additional driver invitation sent" }),
    P({ key: "evidence", kind: "booking", title: "Second driver's documents are attached", data: { documents: "Licence / identity evidence", review: "Business review status kept separate from upload" } }),
    P({ key: "eligible", kind: "confirmed", title: "Driver list ready for release", ok: "Eligibility and pickup document checks remain visible" }),
  ],
};

const legalReadinessFlow = {
  id: "legal-readiness-review",
  category: "operations",
  title: "Business and vehicle Legal Readiness",
  subtitle: "Evidence → review → expiry → operational consequence",
  keywords: ["legal readiness", "vehicle legal", "expiry", "evidence"],
  business: "Manila Motor Rental",
  salesNotes: [
    "ConfirmPH records evidence and review state; it does not present itself as a government certifier.",
    "An expired or unreviewed record creates an operational consequence that staff can act on.",
  ],
  panels: [
    P({ key: "business", kind: "warning", title: "Business Legal Readiness evidence", warn: "Review status and expiry are visible to the owner" }),
    P({ key: "vehicle", kind: "vehicle", title: "Vehicle Legal Readiness record", data: { evidence: "Insurance / registration record", status: "Review required", consequence: "Release may need review" } }),
    P({ key: "action", kind: "confirmed", title: "Operational consequence is recorded", ok: "Staff see the next action without a claim of government certification" }),
  ],
};

const financeBooksFlow = {
  id: "finance-books-rental-close",
  category: "financial",
  title: "Finance & Books: clean return and deposit refund",
  subtitle: "Payment → security deposit liability → return inspection → refund → reconciliation",
  keywords: ["finance", "books", "security deposit", "refund", "reconciliation"],
  business: "Cebu Wheels & Wander",
  salesNotes: [
    "Rental payment, advance, refundable security deposit and remaining balance are distinct records.",
    "A clean return does not automatically charge the customer; the deposit is refunded after inspection.",
    "Records flow into ConfirmPH Books as accountant-ready internal records, not automatic BIR filings.",
  ],
  panels: [
    P({ key: "paid", kind: "booking", title: "Payment records are separated", data: { rental_payment: "Recorded", advance: "Part of rental price", security_deposit: "Refundable liability", balance: "Tracked separately" } }),
    P({ key: "return", kind: "handover", title: "Vehicle return is inspected", type: "return", data: { condition: "No new damage recorded", fuel: "Recorded", mileage: "Recorded" } }),
    P({ key: "refund", kind: "confirmed", title: "Security deposit refund due", ok: "Refund recorded and ready for reconciliation" }),
  ],
};

const staffLeavePayrollFlow = {
  id: "staff-leave-payroll",
  category: "operations",
  title: "Staff leave, attendance and payroll-ready records",
  subtitle: "Schedule → leave approval → driver availability → attendance → compensation snapshot",
  keywords: ["staff", "leave", "attendance", "overtime", "payroll"],
  business: "Iloilo Drive PH",
  salesNotes: [
    "Leave, rest days, schedules and attendance remain separate records.",
    "Approved leave flags an overlapping driver assignment for replacement instead of silently deleting it.",
    "Payroll periods snapshot effective-dated compensation and use integer centavos.",
  ],
  panels: [
    P({ key: "schedule", kind: "booking", title: "Staff schedule and rest days", data: { weekly: "Configured", rest_day: "Sunday", grace: "Late is measured from the scheduled start", driver: "Company driver capability retained" } }),
    P({ key: "leave", kind: "warning", title: "Leave approved with assignment check", warn: "DRIVER REPLACEMENT REQUIRED when an existing assignment overlaps" }),
    P({ key: "attendance", kind: "activity", title: "Attendance compared with the schedule", data: { expected: "09:00 to 18:00", actual: "Clock records", difference: "Late, undertime or absent", correction: "Recorded separately, original kept, employee notified" } }),
    P({ key: "documents", kind: "vehicle", title: "Employee documents", data: { stored: "Private evidence store", state: "Uploaded is not checked", privacy: "Medical certificates stay with the employee and leave management" } }),
    P({ key: "payroll", kind: "confirmed", title: "Payroll-ready period calculated", ok: "Compensation snapshot, gross, deductions, net and remaining payable preserved" }),
    P({ key: "statutory", kind: "warning", title: "Contribution rules are entered, never assumed", warn: "With no SSS, PhilHealth, Pag-IBIG or withholding table entered, nothing is deducted and every payroll item says so" }),
    P({ key: "payslip", kind: "confirmation-sent", title: "Payslip issued to the employee", ok: "Numbered snapshot; a correction reissues a new version and keeps the old one" }),
  ],
};

const damageClaimDispute = {
  id: "damage-claim-dispute-closure",
  category: "financial",
  title: "Damage found at return: charge, dispute and closure",
  subtitle: "Return inspection → damage record → established charge → deposit → dispute → closure",
  keywords: ["damage", "claim", "dispute", "deposit", "insurance", "closure"],
  business: "Manila Motor Rental",
  salesNotes: [
    "A deposit is applied only to a charge that has been established, never to an accusation.",
    "The customer can dispute, and the dispute is part of the rental record rather than a private note.",
    "Insurance is recorded as its own claim; it does not silently replace what the customer owes.",
  ],
  panels: [
    P({ key: "return", kind: "handover", title: "Return inspection records the condition", type: "return", data: { damage: "New scratch recorded with photos", fuel: "Recorded", mileage: "Recorded" } }),
    P({ key: "charge", kind: "booking", title: "Charge is established, not assumed", data: { estimate: "Repair quote attached", customer_notified: "Yes", status: "Awaiting customer response" } }),
    P({ key: "deposit", kind: "warning", title: "Deposit applied against the established charge", warn: "Only the established amount is applied; the remainder stays refundable" }),
    P({ key: "dispute", kind: "incident", title: "Customer disputes the charge", data: { evidence: "Customer photos attached", state: "Under review", effect: "Closure is held until the dispute is resolved" } }),
    P({ key: "insurance", kind: "vehicle", title: "Insurance claim recorded separately", data: { claim: "Filed with the insurer", customer_balance: "Unchanged until the insurer responds" } }),
    P({ key: "closed", kind: "confirmed", title: "Rental closed with a full statement", ok: "Charges, deposit, refund and dispute outcome are all on the closure record" }),
  ],
};

const extensionAndClosure = {
  id: "rental-extension-closure",
  category: "operations",
  title: "Customer extends a rental in progress",
  subtitle: "Extension request → availability check → price → agreement → closure",
  keywords: ["extension", "extend", "availability", "closure"],
  business: "Iloilo Drive PH",
  salesNotes: [
    "An extension is checked against the same availability rules as a new booking, so it cannot double-book the vehicle.",
    "The customer sees the new price and the agreement before the extension is confirmed.",
  ],
  panels: [
    P({ key: "request", kind: "booking", title: "Customer asks to keep the vehicle longer", data: { current_end: "Saturday 10:00", requested_end: "Monday 10:00" } }),
    P({ key: "check", kind: "conflict", title: "Availability is checked for the new dates", data: { conflict: "None for this vehicle", alternative: "Offered when the vehicle is already booked" } }),
    P({ key: "price", kind: "customer-confirmation", title: "New total and agreement are shown before confirming" }),
    P({ key: "closed", kind: "confirmed", title: "Extension confirmed", ok: "One rental record, extended; the original dates stay in the history" }),
  ],
};

export const SCENARIOS = [
  messenger, whatsapp, sms, phone, walkin,
  doubleBooking, tightBuffer, dateChange, awaitingConfirmation, forgottenBooking,
  breakdown, maintenance, lateReturn, replacement, damageIncident,
  followUp, multiStaff, upcomingPickup,
  pickupHandover, returnAndReady, newInquiryAlert,
  depositBalance,
  verifiedReview,
  businessPageIndex, websiteIndex,
  // Owner & Operations
  employeesManagementIdx, staffAvailabilityIdx, vehiclesManagementIdx,
  dailyChecklistsIdx, warningsComplianceIdx,
  directSelfDrive, directWithDriver, additionalDriver,
  legalReadinessFlow, financeBooksFlow, staffLeavePayrollFlow,
  damageClaimDispute, extensionAndClosure,
];

export function findScenario(id) {
  return SCENARIOS.find(s => s.id === id) || null;
}
